import { Box, SplitViewCard, ToolLandingPage, useI18nContext } from "@procore/core-react";
import React, { useEffect, useState } from "react";
import { TimeOffStore } from "@/stores/time-off-store.core";
import { SavedViewStore } from "@/stores/saved-view-store.core";
import { DateUtils } from "@/lib/utils/date";
import { useGroupContext } from "@/react/providers/group-context-provider";
import { TimeOffListDataTable } from "./time-off-list-data-table";
import { getDetachedDay } from "@laborchart-modules/common/dist/datetime";
import type { IServerFilter } from "@procore/data-table";
import type {
   FindTimeOffPaginatedQueryParams,
   FindTimeOffPaginatedResponse,
} from "@laborchart-modules/lc-core-api/dist/api/time-off/find-time-off";
import type { EditTimeOffFormValues } from "./time-off-list-prop-types";
import type { SortModel } from "@/react/prop-types";
import {
   convertDataTableFilters,
   convertDataTableSort,
   columnHeadersMap,
   defaultTimeOffTableConfig,
   filterFieldMap,
   filterRendererMap,
   createTimeOff,
} from "./time-off-list-helpers";
import { convertSavedViewToDataTableConfig, getUserName } from "@/react/shared/helper";
import moment from "moment-timezone";
import { getTimeOffReasons } from "../tearsheets/time-off/constants";
import { useViewPreferenceContext } from "@/react/providers/permission-context-provider";
import "./style.css";
import { streamJobTitles, streamPeople } from "../data-table/data-table-utils";

/* istanbul ignore next */
export const TimeOffListContainer = ({ setTitle }: { setTitle: (title: string) => void }) => {
   const I18n = useI18nContext();
   const [savedView, setSavedView] = useState<any>();
   const { groupId } = useGroupContext();
   const { getViewPreference } = useViewPreferenceContext();

   const url = new URL(window.location.href);
   const params = url.searchParams;
   const [viewId] = useState(params.get("viewId"));

   const isLastNameFirst = getViewPreference()?.displayLastNamesFirst() ?? false;

   const fetchTimeOffList = async (
      filters: IServerFilter[],
      sortModel: SortModel[],
      startingAfter?: string,
      search?: string,
   ) => {
      const coreApiFilters = convertDataTableFilters(filters);
      const coreApiSort = convertDataTableSort(sortModel, isLastNameFirst);
      const params: FindTimeOffPaginatedQueryParams = {
         timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
         limit: 100,
         starting_after: startingAfter ?? undefined,
         search: search,
         sort_by: coreApiSort.sort_by,
         sort_direction: coreApiSort.direction,
         group_id: groupId == "my-groups" ? undefined : groupId,
         filters: coreApiFilters,
      };
      const timeOffData: FindTimeOffPaginatedResponse = await TimeOffStore.findTimeOffPaginated(
         params,
      ).payload;
      const timeOffListDetails = [];

      // TODO: Refactor server methods for testability
      /* istanbul ignore next */
      for (const timeOff of timeOffData.data) {
         const workerName = getUserName(timeOff.person_name, isLastNameFirst);
         const reasonObject =
            timeOff.reason && getTimeOffReasons(I18n).find((x: any) => x.value == timeOff.reason);
         timeOffListDetails.push({
            ...timeOff,
            workers_name: {
               label: workerName,
               href: `/groups/${groupId}/people/${timeOff.person_id}`,
            },
            job_title: {
               label: timeOff.job_title ? timeOff.job_title.name : "",
               color: timeOff.job_title ? timeOff.job_title.color : "",
               shape: "circle",
            },
            start_date: timeOff.start_day ? DateUtils.getAttachedDate(timeOff.start_day) : "",
            end_date: timeOff.end_day ? DateUtils.getAttachedDate(timeOff.end_day) : "",
            daily_start_time:
               timeOff.batch_start_time !== undefined && timeOff.batch_start_time !== null
                  ? DateUtils.formatTimeVal(timeOff.batch_start_time).trim()
                  : "",
            daily_end_time:
               timeOff.batch_end_time !== undefined && timeOff.batch_end_time !== null
                  ? DateUtils.formatTimeVal(timeOff.batch_end_time).trim()
                  : "",
            reason: reasonObject ? reasonObject : timeOff.reason,
            type: timeOff.is_paid
               ? {
                    id: 1,
                    value: "paid",
                    label: I18n.t("views.company.workforce_planning.time_off.paid"),
                    color: "green",
                 }
               : {
                    id: 2,
                    value: "unpaid",
                    label: I18n.t("views.company.workforce_planning.time_off.unpaid"),
                    color: "yellow",
                 },
            repeat: timeOff.repeat ? timeOff.repeat : "",
            occurences: timeOff.occurrences_count ? timeOff.occurrences_count : 0,
            submitted_on: timeOff.created_at ? timeOff.created_at : "",
            person_status: timeOff.person_status,
         });
      }
      return { data: timeOffListDetails, pagination: timeOffData.pagination };
   };

   /* istanbul ignore next */
   const fetchSavedView = React.useCallback(
      async (viewId: string) => {
         const data = await SavedViewStore.getSavedView(viewId).payload;
         setTitle(data.data?.name ?? I18n.t("views.company.workforce_planning.list"));
         setSavedView(data);
      },
      [I18n, setTitle],
   );

   /* istanbul ignore next */
   const bulkUpdateTimeOff = async (ids: string[], values: EditTimeOffFormValues) => {
      const payload = ids.map((x: any) => {
         return {
            id: x,
            is_paid: values.type ? values.type.value == "paid" : undefined,
            reason: values.reason ? values.reason.value : undefined,
            batch_start_time: values.daily_start_time ? values.daily_start_time.value : undefined,
            batch_end_time: values.daily_end_time ? values.daily_end_time.value : undefined,
            start_day: values.start_date ? getDetachedDay(values.start_date) : undefined,
            end_day: values.end_date ? getDetachedDay(values.end_date) : undefined,
         };
      });
      await TimeOffStore.updateTimeOffStream(payload).stream;
   };
   const currentDateTime = moment().format("YYYYMMDD_HHmmss");

   /* istanbul ignore next */
   useEffect(() => {
      if (viewId) {
         fetchSavedView(viewId);
      }
   }, [viewId, fetchSavedView]);

   /* istanbul ignore next */
   return (
      <ToolLandingPage
         className={"border-box time-off-list-detail-page data-table-list-detail-page"}
      >
         <ToolLandingPage.Main className="time-off-list-page-main">
            <ToolLandingPage.Body className="time-off-list-page-body">
               <SplitViewCard className="time-off-list-card">
                  <SplitViewCard.Section className="time-off-list-section data-table-list-section">
                     <Box
                        style={{
                           height: "100%",
                           marginTop: "5px",
                        }}
                     >
                        {!viewId && (
                           <TimeOffListDataTable
                              fetchTimeOffList={fetchTimeOffList}
                              groupId={groupId}
                              streamPeople={streamPeople}
                              createTimeOff={createTimeOff}
                              savedView={null}
                              streamJobTitles={streamJobTitles}
                              currentDateTime={currentDateTime}
                              bulkUpdateTimeOff={bulkUpdateTimeOff}
                           />
                        )}
                        {/* If the view ID is present, wait until the saved view is loaded from the API to render the data table */}
                        {viewId && savedView && (
                           <TimeOffListDataTable
                              fetchTimeOffList={fetchTimeOffList}
                              groupId={groupId}
                              streamPeople={streamPeople}
                              createTimeOff={createTimeOff}
                              savedView={convertSavedViewToDataTableConfig(
                                 savedView.data,
                                 columnHeadersMap,
                                 defaultTimeOffTableConfig,
                                 filterFieldMap,
                                 filterRendererMap,
                              )}
                              streamJobTitles={streamJobTitles}
                              currentDateTime={currentDateTime}
                              bulkUpdateTimeOff={bulkUpdateTimeOff}
                           />
                        )}
                     </Box>
                  </SplitViewCard.Section>
               </SplitViewCard>
            </ToolLandingPage.Body>
         </ToolLandingPage.Main>
      </ToolLandingPage>
   );
};
